import { Type, Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  RelationOne,
  RelationField,
  StringField,
  TextField,
  IntegerField,
  DecimalField,
  TimestampField,
  EnumField,
  ArrayField,
  JsonField,
  BooleanField,
  UuidField,
} from '@ayocore/platform/common/helpers/schema-types';
import partnerSchemaDefinition from './partner.schema';
import leadStatusSchemaDefinition from './lead-status.schema';
import userSchemaDefinition from '@ayocore/platform/identity/schemas/user.schema';
import leadTypeSchemaDefinition from './lead-type.schema';
import dealSchemaDefinition from './deal.schema';
import salesAreaSchemaDefinition from './sales-area.schema';
import leadSourceSchemaDefinition from './lead-source.schema';
import lostReasonSchemaDefinition from './lost-reason.schema';

import { name as moduleName } from '../module.json';
/**
 * Lead Schema - Sales opportunities and potential deals
 *
 * Represents sales leads with:
 * - Many-to-one relationship with partners (optional - can be added later)
 * - Many-to-one relationship with lead statuses (required)
 * - Many-to-many relationship with contact persons (via junction table)
 */

// Complete schema (internal use only)
const LeadSchema = Type.Object({
  ...SystemFields, // Platform-provided system fields (id, created_at, updated_at)

  title: StringField({
    title: 'Название лида',
    minLength: 1,
    maxLength: 255,
    description: 'Название/заголовок лида',
  }),

  description: TextField({
    title: 'Описание',
    nullable: true,
    description: 'Подробное описание лида',
  }),

  // ✨ Relations - TypeScript types and FK fields
  partner: RelationOne(partnerSchemaDefinition), // For TypeScript type
  partner_id: RelationField(partnerSchemaDefinition, 'ID партнера', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'ID компании, с которой связан лид',
    relationName: 'partner',
  }),

  lead_type: RelationOne(leadTypeSchemaDefinition), // For TypeScript type
  lead_type_id: RelationField(leadTypeSchemaDefinition, 'ID типа лида', {
    description: 'ID типа лида',
    relationName: 'lead_type',
  }),

  lead_status: RelationOne(leadStatusSchemaDefinition), // For TypeScript type
  lead_status_id: RelationField(leadStatusSchemaDefinition, 'ID статуса лида', {
    description: 'ID текущего статуса лида',
    relationName: 'lead_status',
  }),

  manager: RelationOne(userSchemaDefinition), // For TypeScript type
  manager_id: RelationField(userSchemaDefinition, 'Ответственный', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Ответственный менеджер за ведение лида',
    relationName: 'manager',
  }),

  // Lead financial details
  amount: IntegerField({
    title: 'Потенциал (сумма)',
    nullable: true,
    minimum: 0,
    description: 'Ожидаемая стоимость сделки в рублях (целое число). Сделки ещё нет — это потенциал лида; вместе с probability формирует weighted pipeline.',
  }),

  currency: StringField({
    title: 'Валюта',
    maxLength: 3,
    default: 'RUB',
    description: 'Код валюты (ISO 4217)',
  }),

  probability: DecimalField({
    title: 'Вероятность закрытия (%)',
    minimum: 0,
    maximum: 100,
    default: 50,
    precision: 5,
    scale: 2,
    description: 'Вероятность закрытия сделки (0-100%)',
  }),

  // Date tracking
  expected_close_date: TimestampField({
    title: 'Ожидаемая дата закрытия',
    nullable: true,
    description: 'Ожидаемая дата закрытия сделки',
  }),

  actual_close_date: TimestampField({
    title: 'Фактическая дата закрытия',
    nullable: true,
    description: 'Фактическая дата закрытия сделки',
  }),

  // Полиморфный источник лида (письмо, звонок, Telegram и т.п.)
  source_entity_type: StringField({
    title: 'Тип источника',
    nullable: true,
    maxLength: 321,
    description:
      'Код репозитория источника из core_repositories.repository_code (mailer::IncomingEmailRepository, calls::CallRecord и т.п.)',
  }),
  source_entity_uuid: UuidField({
    title: 'UUID источника',
    nullable: true,
    description: 'UUID записи-источника (письмо, звонок, сообщение и т.п.)',
  }),

  notes: TextField({
    title: 'Примечания',
    nullable: true,
    description: 'Внутренние заметки о лиде',
  }),

  // NEW FIELDS FOR TESTING
  priority: EnumField(['low', 'medium', 'high', 'urgent'], {
    title: 'Приоритет',
    default: 'medium',
    description: 'Приоритет лида (низкий, средний, высокий, срочный)',
  }),

  tags: ArrayField(Type.String(), {
    title: 'Теги',
    nullable: true,
    description: 'Теги для категоризации лида',
  }),

  metadata: JsonField(undefined, {
    title: 'Метаданные',
    nullable: true,
    description: 'Дополнительные структурированные данные',
  }),

  // TEST FIELDS FOR SYNC
  test_field: StringField({
    title: 'Тестовое поле',
    nullable: true,
    maxLength: 50,
    description: 'Тестовое поле для синхронизации',
  }),

  test_number: IntegerField({
    title: 'Тестовое число',
    nullable: false,
    default: 0,
    description: 'Тестовое числовое поле',
  }),

  test_bool: BooleanField({
    title: 'Тестовый флаг',
    default: false,
    description: 'Тестовое логическое поле',
  }),
  is_archived: BooleanField({
    title: 'Архивный',
    default: false,
    description: 'Архивная сделка',
  }),

  created_by: RelationOne(userSchemaDefinition),
  created_by_id: RelationField(userSchemaDefinition, 'Кем добавлен', {
    nullable: true,
    onDelete: 'SET NULL',
    relationName: 'created_by',
  }),

  updated_by: RelationOne(userSchemaDefinition),
  updated_by_id: RelationField(userSchemaDefinition, 'Кем обновлён', {
    nullable: true,
    onDelete: 'SET NULL',
    relationName: 'updated_by',
  }),

  // Связь с сделкой (создана из лида)
  deal: RelationOne(dealSchemaDefinition),
  deal_id: RelationField(dealSchemaDefinition, 'ID сделки', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Сделка, созданная из данного лида',
    relationName: 'deal',
  }),

  // Регион / территория
  sales_area: RelationOne(salesAreaSchemaDefinition),
  sales_area_id: RelationField(salesAreaSchemaDefinition, 'Регион', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Территория / регион лида',
    relationName: 'sales_area',
  }),

  // Источник лида (канал входящей заявки)
  lead_source: RelationOne(leadSourceSchemaDefinition),
  lead_source_id: RelationField(leadSourceSchemaDefinition, 'Источник лида', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Канал / источник входящей заявки',
    relationName: 'lead_source',
  }),

  // Причина отказа (заполняется при закрытии лида в LOST)
  lost_reason: RelationOne(lostReasonSchemaDefinition),
  lost_reason_id: RelationField(lostReasonSchemaDefinition, 'Причина отказа', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Причина закрытия лида в негативном финальном статусе',
    relationName: 'lost_reason',
  }),

  lost_comment: TextField({
    title: 'Комментарий к причине отказа',
    nullable: true,
    description: 'Свободный комментарий менеджера к причине отказа',
  }),

  // Обратная связь от клиента (заполняется при закрытии лида в позитивный финальный статус)
  won_comment: TextField({
    title: 'Обратная связь клиента',
    nullable: true,
    description: 'Свободный комментарий менеджера с фидбеком клиента при позитивном закрытии лида',
  }),

  search_string: TextField({
    title: 'Строка поиска',
    nullable: true,
    description:
      'Email-адреса и телефоны (только цифры) привязанных каналов. Заполняется автоматически через ContactChannelRepository.',
  }),
});

// TypeScript types derived from schemas
export type Lead = Static<typeof LeadSchema>;
/**
 * Тип данных для создания лида.
 * Исключаем только системные поля, relation-поля остаются (репозиторий ожидает их для корректной работы).
 */
export type CreateLead = Omit<Lead, 'id' | 'uuid' | 'created_at' | 'updated_at' | 'sort_order'>;

/**
 * Тип данных для обновления лида.
 * Все поля из CreateLead делаем необязательными (включая relation-поля).
 */
export type UpdateLead = Partial<CreateLead>;

// ✨ CLEAN: Schema definition with automatic table prefixing and form schemas
export default defineSchema({
  moduleName,
  tableName: 'leads', // System will prefix with 'crm_'
  softDelete: true,
  schema: LeadSchema,
  additionalIndexes: [
    'title',
    'search_string',
    'partner_id',
    'lead_status_id',
    'manager_id',
    'expected_close_date',
    'source_entity_type',
    'source_entity_uuid',
    'deal_id',
    'sales_area_id',
    'lead_source_id',
    'lost_reason_id',
  ],
  foreignKeys: [
    {
      column: 'source_entity_type',
      references: { table: 'core_repositories', column: 'repository_code' },
      onDelete: 'SET NULL',
    },
  ],
  dependencies: [
    'crm_partners',
    'crm_lead_statuses',
    'identity_users',
    'core_repositories',
    'crm_deals',
    'sales_areas',
    'crm_lead_sources',
    'crm_lost_reasons',
  ],
});
